// D84 — Page /demandes-achat : liste + workflow visible + edit/voir (P27c)
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { demandesAchatApi } from "@/lib/api";
import { useCan } from "@/hooks/useCan";
import { useToast } from "@/components/ui/toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import CreateDAModal from "@/components/da/CreateDAModal";
import { Eye, Pencil, FileText, FileSpreadsheet, Check, X, Plus } from "lucide-react";
import { ht } from "@/lib/ht";
import { useT } from "@/lib/i18n";

export default function ListeDemandesAchatPage() {
  const t = useT();
  const qc = useQueryClient();
  const { toast } = useToast();
  const canValidate = useCan("validate");
  const canWrite = useCan("write");
  const [showCreate, setShowCreate] = useState(false);
  const [view, setView] = useState<import("@/types/da").DemandeAchatDetail | null>(null);
  const [edit, setEdit] = useState<import("@/types/da").DemandeAchatDetail | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["demandes-achat"],
    queryFn: () => demandesAchatApi.list(),
  });

  const validateMutation = useMutation({
    mutationFn: (id: number) => demandesAchatApi.valider(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["demandes-achat"] });
      toast({ title: ht("DA validée"), variant: "success" });
    },
    onError: (err) =>
      toast({ title: ht("Erreur validation"), description: (err as Error).message, variant: "error" }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => demandesAchatApi.annuler(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["demandes-achat"] });
      toast({ title: ht("DA annulée"), variant: "success" });
    },
    onError: (err) =>
      toast({ title: ht("Erreur annulation"), description: (err as Error).message, variant: "error" }),
  });

  const handleView = async (id: number) => {
    try {
      const da = await demandesAchatApi.get(id);
      setView(da);
    } catch (err) {
      toast({ title: "Erreur", description: (err as Error).message, variant: "error" });
    }
  };

  const handleEdit = async (id: number) => {
    try {
      const da = await demandesAchatApi.get(id);
      setEdit(da);
    } catch (err) {
      toast({ title: "Erreur", description: (err as Error).message, variant: "error" });
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{ht("Demandes d'Achat")}
          </h1>
          <p className="text-muted-foreground">
            {t("da.workflow_hint")}
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Nouvelle DA
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("common.list")} ({data?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">{ht("Chargement...")}</div>
          ) : error ? (
            <div className="py-8 text-red-600">{(error as Error).message}</div>
          ) : data && data.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Aucune DA.
            </div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">{ht("Type")}</th>
                  <th className="px-3 py-2">{ht("Objet")}</th>
                  <th className="px-3 py-2">{ht("Statut")}</th>
                  <th className="px-3 py-2">{ht("Créée le")}</th>
                  <th className="px-3 py-2">{ht("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {data?.map((da) => (
                  <tr key={da.id} className="border-b hover:bg-muted/30">
                    <td className="px-3 py-2 font-mono font-semibold">
                      {da.code}
                    </td>
                    <td className="px-3 py-2">{da.type}</td>
                    <td className="px-3 py-2 max-w-md truncate">{da.objet}</td>
                    <td className="px-3 py-2">
                      <StatutBadge statut={da.statut} />
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(da.created_at).toLocaleDateString("fr-FR")}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleView(da.id)}
                          title={ht("Voir détails")}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {canWrite && da.statut === "emise" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(da.id)}
                            title={ht("Éditer")}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            window.open(
                              demandesAchatApi.exportPdfUrl(da.id),
                              "_blank",
                            )
                          }
                          title={ht("Télécharger PDF")}
                        >
                          <FileText className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            window.open(
                              demandesAchatApi.exportExcelUrl(da.id),
                              "_blank",
                            )
                          }
                          title={ht("Télécharger Excel")}
                        >
                          <FileSpreadsheet className="h-3.5 w-3.5" />
                        </Button>
                        {canValidate && da.statut === "emise" && (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => validateMutation.mutate(da.id)}
                              disabled={validateMutation.isPending}
                              title={ht("Valider")}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => cancelMutation.mutate(da.id)}
                              disabled={cancelMutation.isPending}
                              title={ht("Annuler")}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </CardContent>
      </Card>

      {showCreate && <CreateDAModal onClose={() => setShowCreate(false)} />}
      {view && <ViewDAModal da={view} onClose={() => setView(null)} />}
      {edit && <EditDAModal da={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

function ViewDAModal({
  da,
  onClose,
}: {
  da: import("@/types/da").DemandeAchatDetail;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(40rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>
            Détail DA {da.code}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-muted-foreground">{ht("Type")}</div>
                <div className="font-semibold">{da.type}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{ht("Statut")}</div>
                <StatutBadge statut={da.statut} />
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{ht("Objet")}</div>
              <div className="font-semibold">{da.objet}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{ht("Motif")}</div>
              <div className="rounded-md bg-muted p-2 text-xs">{da.motif}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">
                Lignes ({da.lignes?.length ?? 0})
              </div>
              {da.lignes && da.lignes.length > 0 ? (
                <div className="overflow-x-auto"><table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="px-2 py-1 text-left">{ht("Désignation")}</th>
                      <th className="px-2 py-1 text-right">{ht("Qté")}</th>
                      <th className="px-2 py-1">{ht("Unité")}</th>
                      <th className="px-2 py-1 text-right">{ht("P.U.")}</th>
                      <th className="px-2 py-1 text-right">{ht("Total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {da.lignes.map((l) => (
                      <tr key={l.id} className="border-b">
                        <td className="px-2 py-1">{l.designation}</td>
                        <td className="px-2 py-1 text-right">{l.quantite}</td>
                        <td className="px-2 py-1">{l.unite}</td>
                        <td className="px-2 py-1 text-right">
                          {l.prix_unitaire_fcfa.toLocaleString("fr-FR")} FCFA
                        </td>
                        <td className="px-2 py-1 text-right font-mono">
                          {(l.quantite * l.prix_unitaire_fcfa).toLocaleString("fr-FR")}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={4} className="px-2 py-1 text-right font-semibold">{ht("Total")}
                      </td>
                      <td className="px-2 py-1 text-right font-mono font-semibold">
                        {da.lignes
                          .reduce((acc, l) => acc + l.quantite * l.prix_unitaire_fcfa, 0)
                          .toLocaleString("fr-FR")} FCFA
                      </td>
                    </tr>
                  </tbody>
                </table></div>
              ) : (
                <div className="text-xs text-muted-foreground">{ht("Aucune ligne.")}</div>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              Créée le {new Date(da.created_at).toLocaleString("fr-FR")}
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => window.open(demandesAchatApi.exportPdfUrl(da.id), "_blank")}>
              PDF
            </Button>
            <Button onClick={onClose}>{ht("Fermer")}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EditDAModal({
  da,
  onClose,
}: {
  da: import("@/types/da").DemandeAchatDetail;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [objet, setObjet] = useState(da.objet);
  const [motif, setMotif] = useState(da.motif);
  const [type, setType] = useState<"approvisionnement" | "urgence">(da.type as "approvisionnement" | "urgence");

  const update = useMutation({
    mutationFn: () =>
      demandesAchatApi.update(da.id, {
        objet,
        motif,
        type,
        lignes: da.lignes.map((l) => ({
          article_id: l.article_id,
          designation: l.designation,
          quantite: l.quantite,
          unite: l.unite,
          fournisseur_id: l.fournisseur_id,
          prix_unitaire_fcfa: l.prix_unitaire_fcfa,
        })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["demandes-achat"] });
      onClose();
      toast({ title: ht("DA modifiée"), variant: "success" });
    },
    onError: (err) =>
      toast({ title: ht("Erreur modification"), description: (err as Error).message, variant: "error" }),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(40rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>Modifier DA {da.code}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <label className="text-xs">{ht("Type")}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value as "approvisionnement" | "urgence")}
              >
                <option value="approvisionnement">{ht("Approvisionnement")}</option>
                <option value="urgence">{ht("Urgence")}</option>
              </select>
            </div>
            <div>
              <label className="text-xs">{ht("Objet *")}</label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={objet}
                onChange={(e) => setObjet(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs">{ht("Motif *")}</label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={3}
              />
            </div>
            <div className="text-xs text-muted-foreground">
              ⚠️ Les lignes ne sont pas modifiables via cette UI. Créez une nouvelle DA si besoin.
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={update.isPending}>{ht("Annuler")}
            </Button>
            <Button
              onClick={() => update.mutate()}
              disabled={update.isPending || !objet.trim() || !motif.trim()}
            >
              {update.isPending ? "En cours..." : "Modifier"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatutBadge({ statut }: { statut: string }) {
  const styles: Record<string, string> = {
    emise: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40",
    validee: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40",
    annulee: "bg-destructive/15 text-destructive dark:text-red-300 border border-destructive/40 line-through",
  };
  const labels: Record<string, string> = {
    emise: ht("Émise"),
    validee: ht("Validée"),
    annulee: ht("Annulée"),
  };
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs ${styles[statut] ?? "bg-gray-100"}`}
    >
      {labels[statut] ?? statut}
    </span>
  );
}
